import { useState, useEffect } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@os/integrations/supabase/client';

export interface UserProfile {
  id: string;
  user_id: string;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
  created_at: string;
  updated_at: string;
  full_name?: string | null; // added for main Profiles table
  email?: string | null;     // optional: available in main Profiles table
}

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log('useAuth: Setting up auth listener');
    let initialLoadComplete = false;
    
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('useAuth: Auth state changed - event:', event, 'session:', !!session, 'user:', session?.user?.email);
        
        // Handle explicit logout events
        if (event === 'SIGNED_OUT') {
          console.log('useAuth: SIGNED_OUT event - clearing all state');
          setSession(null);
          setUser(null);
          setProfile(null);
          if (!initialLoadComplete) {
            setLoading(false);
            initialLoadComplete = true;
          }
          return;
        }
        
        // Update state immediately for other events
        setSession(session);
        setUser(session?.user ?? null);
        
        // Handle profile fetching
        if (session?.user) {
          setTimeout(async () => {
            try {
              const { data: profileData } = await supabase
                .from('profiles')
                .select('id, username, full_name, avatar_url, email, created_at, updated_at')
                .eq('id', session.user.id)
                .single();
              
              setProfile(profileData as any);
            } catch (error) {
              console.error('Error fetching profile:', error);
            }
          }, 0);
        } else {
          setProfile(null);
        }
        
        // Only set loading to false once after initial setup
        if (!initialLoadComplete) {
          console.log('useAuth: Setting loading to false (initial)');
          setLoading(false);
          initialLoadComplete = true;
        }
      }
    );

    // Then check for existing session (this will trigger the auth state change above)
    console.log('useAuth: Checking for existing session');
    supabase.auth.getSession().catch((error) => {
      console.error('useAuth: getSession error:', error);
      if (!initialLoadComplete) {
        setLoading(false);
        initialLoadComplete = true;
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, username: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          username,
          display_name: username,
          full_name: username // ensure profiles.full_name is set via signup trigger
        }
      }
    });
    
    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    
    return { error };
  };

  const signOut = async () => {
    console.log('useAuth: signOut called');
    setLoading(true);
    
    try {
      // Try to sign out from Supabase
      console.log('useAuth: Calling Supabase signOut');
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.warn('useAuth: Supabase signOut error (continuing anyway):', error);
      } else {
        console.log('useAuth: Supabase signOut completed successfully');
      }
    } catch (err) {
      console.error('useAuth: signOut error (continuing anyway):', err);
    }
    
    // Force clear all state regardless of Supabase response
    console.log('useAuth: Force clearing all auth state');
    setUser(null);
    setSession(null);
    setProfile(null);
    setLoading(false);
    
    console.log('useAuth: signOut complete - all state cleared');
    return { error: null };
  };

  return {
    user,
    session,
    profile,
    loading,
    signUp,
    signIn,
    signOut
  };
};